// client/src/pages/GuildPage.jsx
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Shield, Crown, Users, MessageCircle, LogOut, UserMinus } from 'lucide-react';
import { api, compact } from '../lib/api';
import { useAuth } from '../lib/store';
import { Avatar, SkeletonList, SectionHead } from '../components/ui';

export default function GuildPage() {
  const { handle } = useParams();
  const nav = useNavigate();
  const { user, guild: myGuild } = useAuth();
  const h = decodeURIComponent(handle);

  const { data: g, isLoading, refetch } = useQuery({ queryKey: ['guild', h], queryFn: () => api.get(`/guilds/${encodeURIComponent(h)}`).then(r => r.data) });

  const joinM = useMutation({ mutationFn: () => api.post(`/guilds/${g.id}/join`), onSuccess: async () => { toast.success('Joined! 🛡️'); await useAuth.getState().load(); refetch(); }, onError: e => toast.error(e.message) });
  const leaveM = useMutation({ mutationFn: () => api.post('/guilds/leave'), onSuccess: async () => { toast('Left the guild'); await useAuth.getState().load(); nav('/guilds'); } });
  const kickM = useMutation({ mutationFn: (uid) => api.post(`/guilds/${g.id}/kick/${uid}`), onSuccess: () => { toast.success('Removed'); refetch(); } });

  if (isLoading || !g) return <SkeletonList n={3} h="h-40" />;
  const iAmMember = g.members.some(m => m.id === user.id);
  const myRole = g.members.find(m => m.id === user.id)?.role;

  return (
    <div className="space-y-4 animate-fade-up">
      <div className="card overflow-hidden">
        <div className="p-5 bg-gradient-to-br from-grape-600 to-indigo-800 text-white">
          <div className="flex items-center gap-4">
            <div className="w-20 h-20 rounded-2xl bg-white/15 ring-4 ring-white/30 grid place-items-center font-black text-xl shrink-0 overflow-hidden">
              {g.emblem_url ? <img src={g.emblem_url} className="w-full h-full object-cover" alt="" /> : g.tag}
            </div>
            <div className="flex-1 min-w-0">
              <h1 className="font-display text-2xl font-extrabold truncate">{g.name}</h1>
              <p className="text-white/80 text-sm font-semibold">{g.handle} · [{g.tag}]</p>
              <div className="flex gap-1.5 mt-2 text-[11px] font-black">
                <span className="bg-white/20 px-2 py-1 rounded-lg"><Users size={9} className="inline" /> {g.members_count}/{g.max_members}</span>
                <span className="bg-white/20 px-2 py-1 rounded-lg">💎 {compact(g.guild_points)}</span>
                <span className="bg-white/20 px-2 py-1 rounded-lg">⚔️ {g.guild_elo}</span>
              </div>
            </div>
          </div>
          {g.description && <p className="text-sm text-white/85 mt-3">{g.description}</p>}
        </div>

        <div className="p-3 grid grid-cols-2 gap-2">
          {iAmMember ? (
            <>
              <Link to={`/chat/guild/${g.id}`} className="btn-dark btn-sm"><MessageCircle size={14} /> Guild chat</Link>
              <button onClick={() => confirm('Leave guild?') && leaveM.mutate()} className="btn-ghost btn-sm text-red-600">
                <LogOut size={14} /> Leave</button>
            </>
          ) : !myGuild && g.is_open && g.members_count < g.max_members ? (
            <button onClick={() => joinM.mutate()} className="btn-primary col-span-2">Join guild</button>
          ) : (
            <p className="col-span-2 text-center text-xs text-ink-400 font-bold py-2">
              {myGuild ? 'Leave your current guild to join' : g.is_open ? 'Guild is full' : 'Invite only'}
            </p>
          )}
        </div>
      </div>

      <SectionHead title="Members" sub="Sorted by contribution" />
      <div className="card p-2 space-y-1">
        {g.members.map(m => (
          <div key={m.id} className="flex items-center gap-3 p-2.5 rounded-xl hover:bg-ink-50">
            <Link to={`/u/${m.username}`} className="flex items-center gap-3 flex-1 min-w-0">
              <Avatar src={m.image_url || m.avatar_url} name={m.username} size={38} square />
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1.5">
                  <p className="font-bold text-sm truncate">{m.hero_name || m.username}</p>
                  {m.role === 'leader' && <Crown size={11} className="text-amber-500" />}
                  {m.role === 'officer' && <Shield size={11} className="text-grape-500" />}
                </div>
                <p className="text-[11px] text-ink-400 font-semibold">L{m.level} · ELO {m.elo} · {m.wins}W/{m.losses}L</p>
              </div>
            </Link>
            <div className="text-right">
              <p className="font-black text-xs text-grape-600">{compact(m.contributed)}</p>
              <p className="text-[9px] text-ink-400 font-bold">CONTRIB</p>
            </div>
            {myRole === 'leader' && m.id !== user.id && (
              <button onClick={() => confirm(`Kick ${m.username}?`) && kickM.mutate(m.id)}
                className="p-1.5 rounded-lg text-red-500 hover:bg-red-50"><UserMinus size={14} /></button>)}
          </div>))}
      </div>
    </div>
  );
}